/**
 * Eksportene under «festival-cross» i katalogen (docs/EKSPORT.md §4, listene på tvers).
 * API-halvdelen: `apps/api/src/export/definitions/festival-cross/`.
 *
 * Alle er tabeller for en årgang: `parent` = årgangen, `selection` = merkede programposter i
 * lineupen. De startes fra lineupen og fra Oppsummering, og krever det Oppsummering krever
 * (`performance:list` uten en rad), så en gjest kommer ikke inn — som på Oppsummering.
 */
import type { ExportColumnMeta, ExportMeta } from '../types.js';

const C = 'export.festivalCross.';

const column = (
  list: string,
  key: string,
  type: ExportColumnMeta['type'],
  defaultHidden = false,
): ExportColumnMeta => ({
  key,
  labelKey: `${C}${list}.columns.${key}`,
  type,
  ...(defaultHidden ? { defaultHidden: true } : {}),
});

const crossList = (
  name: string,
  list: string,
  columns: readonly ExportColumnMeta[],
  landscape = true,
): ExportMeta => ({
  id: `festival.${name}`,
  domain: 'festival',
  kind: 'table',
  titleKey: `${C}${list}.title`,
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['parent', 'selection'],
  requires: ['performance:list'],
  guest: false,
  columns,
  params: ['editionId'],
  landscape,
});

/** REISELISTE: alle ankomster og avreiser sortert på tid, gruppert på dag. */
const TRAVEL_LIST = crossList('travel-list', 'travelList', [
  column('travelList', 'day', 'text'),
  column('travelList', 'time', 'time'),
  column('travelList', 'kind', 'status'),
  column('travelList', 'mode', 'text'),
  column('travelList', 'reference', 'text'),
  column('travelList', 'from', 'text'),
  column('travelList', 'to', 'text'),
  column('travelList', 'forWhom', 'text'),
  column('travelList', 'count', 'integer'),
  column('travelList', 'passengers', 'multiline'),
  column('travelList', 'contact', 'text'),
  column('travelList', 'contactPhone', 'phone'),
  column('travelList', 'departs', 'time', true),
  column('travelList', 'arrives', 'time', true),
  column('travelList', 'bookingReference', 'text', true),
  column('travelList', 'note', 'multiline', true),
  column('travelList', 'date', 'date', true),
]);

/**
 * KJØRELISTE: lokaltransport og henting sortert på tid, gruppert per sjåfør. Uten sjåførkolonnen
 * (eller med `params.groupBy = 'vehicle'`) grupperes den per bil.
 */
const DRIVING_LIST = crossList('driving-list', 'drivingList', [
  column('drivingList', 'driver', 'text'),
  column('drivingList', 'vehicle', 'text'),
  column('drivingList', 'date', 'date'),
  column('drivingList', 'time', 'time'),
  column('drivingList', 'from', 'text'),
  column('drivingList', 'to', 'text'),
  column('drivingList', 'forWhom', 'text'),
  column('drivingList', 'count', 'integer'),
  column('drivingList', 'passengers', 'multiline'),
  column('drivingList', 'contact', 'text'),
  column('drivingList', 'contactPhone', 'phone'),
  column('drivingList', 'note', 'multiline'),
  column('drivingList', 'arrives', 'time', true),
]);

/** GJESTELISTE for hele festivalen, per dag og programpost. */
const GUEST_LIST = crossList(
  'guest-list',
  'guestList',
  [
    column('guestList', 'day', 'text'),
    column('guestList', 'performance', 'text'),
    column('guestList', 'startsAt', 'time'),
    column('guestList', 'name', 'text'),
    column('guestList', 'kind', 'status'),
    column('guestList', 'seats', 'integer'),
    column('guestList', 'arrivedSeats', 'integer'),
    column('guestList', 'note', 'multiline'),
    column('guestList', 'stage', 'text', true),
    column('guestList', 'arrivedAt', 'time', true),
  ],
  false,
);

/**
 * KOSTHOLD OG ALLERGIER: samlet per dag og måltid. To tabeller i samme eksport — sammendraget
 * (antall per type, fast) og navnelista (`main`, kolonnene velges).
 */
const DIET_LIST = crossList('diet-list', 'dietList', [
  column('dietList', 'day', 'text'),
  column('dietList', 'time', 'time'),
  column('dietList', 'meal', 'text'),
  column('dietList', 'forWhom', 'text'),
  column('dietList', 'name', 'text'),
  column('dietList', 'food', 'multiline'),
  column('dietList', 'role', 'text', true),
]);

/* Persontypene §1: kjøkkenlista kan ta med de godkjente frivillige, for den som ser dem. */
const DIET_LIST_WITH_OPTIONS: ExportMeta = {
  ...DIET_LIST,
  options: [
    {
      key: 'includeVolunteers',
      labelKey: `${C}dietList.options.includeVolunteers`,
      requires: ['volunteer:list'],
    },
  ],
};

/** KONTAKTLISTE FOR ARTISTENE: tour manager, tekniker og agent per artist. */
const ARTIST_CONTACTS = crossList(
  'artist-contacts',
  'artistContacts',
  [
    column('artistContacts', 'artist', 'text'),
    column('artistContacts', 'function', 'text'),
    column('artistContacts', 'name', 'text'),
    column('artistContacts', 'role', 'text'),
    column('artistContacts', 'phone', 'phone'),
    column('artistContacts', 'email', 'email'),
    column('artistContacts', 'group', 'text', true),
    column('artistContacts', 'stage', 'text', true),
  ],
  false,
);

/** TEKNISK OVERSIKT: rider, stage plot og inputliste per artist, og leveransestatus. */
const TECHNICAL_OVERVIEW = crossList('technical-overview', 'technicalOverview', [
  column('technicalOverview', 'artist', 'text'),
  column('technicalOverview', 'stage', 'text'),
  column('technicalOverview', 'startsAt', 'datetime'),
  column('technicalOverview', 'rider', 'status'),
  column('technicalOverview', 'stagePlot', 'status'),
  column('technicalOverview', 'inputList', 'status'),
  column('technicalOverview', 'confirmed', 'integer'),
  column('technicalOverview', 'ordered', 'integer'),
  column('technicalOverview', 'pending', 'integer'),
  column('technicalOverview', 'unavailable', 'integer'),
  column('technicalOverview', 'deviations', 'integer'),
  column('technicalOverview', 'documents', 'multiline', true),
]);

/**
 * MÅLTIDSLISTE (utover §4): Oppsummering › Mat som tabell — dag, tid, sted, hvem, hvor mange som
 * spiser og hva som serveres. Kjøkkenets produksjonsark.
 */
const MEAL_LIST = crossList('meal-list', 'mealList', [
  column('mealList', 'day', 'text'),
  column('mealList', 'time', 'time'),
  column('mealList', 'place', 'text'),
  column('mealList', 'forWhom', 'text'),
  column('mealList', 'eaters', 'integer'),
  column('mealList', 'dishes', 'multiline'),
  column('mealList', 'dietary', 'multiline'),
  column('mealList', 'pax', 'integer', true),
  column('mealList', 'partySize', 'integer', true),
]);

/**
 * LEVERANSELISTE (utover §4): alle leveransene i advancene, per leverandør — det lyd-, backline- og
 * cateringleverandøren skal ha, med artist og status.
 */
const DELIVERY_LIST = crossList('delivery-list', 'deliveryList', [
  column('deliveryList', 'supplier', 'text'),
  column('deliveryList', 'artist', 'text'),
  column('deliveryList', 'section', 'text'),
  column('deliveryList', 'category', 'text'),
  column('deliveryList', 'delivery', 'text'),
  column('deliveryList', 'specification', 'multiline'),
  column('deliveryList', 'status', 'status'),
  column('deliveryList', 'note', 'multiline'),
  column('deliveryList', 'stage', 'text', true),
  column('deliveryList', 'startsAt', 'datetime', true),
]);

export const FESTIVAL_CROSS_EXPORTS: readonly ExportMeta[] = [
  TRAVEL_LIST,
  DRIVING_LIST,
  GUEST_LIST,
  DIET_LIST_WITH_OPTIONS,
  ARTIST_CONTACTS,
  TECHNICAL_OVERVIEW,
  MEAL_LIST,
  DELIVERY_LIST,
];
